// src/utils/deviceDedup.js
// Cihaz seri numarası mükerrer kayıt denetimi için ortak yardımcılar.
// Tüm cihaz EKLEME yolları bu util'i kullanır; böylece "aynı cihazdan birçok
// cihaz numarası" sorunu tek ve tutarlı bir normalize mantığıyla engellenir.
//
// NOT: Bu util yalnızca YENİ mükerrer kaydı ENGELLER (uyar + engelle). Mevcut
// veriye dokunmaz; var olan mükerrerler Cihazlar sayfasındaki salt-okunur
// "Mükerrerler" filtresiyle elle temizlenir.

import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase';

/**
 * Seri numarasını karşılaştırma için normalize eder:
 * büyük harfe çevirir ve harf/rakam dışındaki her karakteri (tire, boşluk, nokta
 * vb.) siler. Böylece "A-123", "a 123" ve "A123" aynı kabul edilir.
 * @param {*} serial
 * @returns {string}
 */
export const normalizeSerial = (serial) => {
    if (!serial) return '';
    return serial.toString().toUpperCase().replace(/[^A-Z0-9]/g, '');
};

/**
 * Verilen seri numarasına sahip mevcut bir cihaz var mı diye kontrol eder
 * (normalize edilmiş, büyük/küçük harf ve ayraç duyarsız karşılaştırma).
 *
 * Normalize edilmiş seri numarası `seriNumarasiNormalized` alanında saklanır ve
 * sunucu tarafındaki onDeviceNormalize trigger'ı tarafından güncel tutulur
 * (bkz. functions/triggers/normalizeTriggers.js). Bu sayede tüm koleksiyonu
 * okumak yerine tek hedefli sorgu yapılır: 1156 okuma -> ~1 okuma.
 *
 * Boş/anlamsız seri numarasında dedup yapılmaz (null döner).
 *
 * @param {string} seriNumarasi
 * @param {object} [opts]
 * @param {string} [opts.excludeId] - hariç tutulacak cihaz id'si (düzenleme modu için kendi kaydı)
 * @returns {Promise<object|null>} bulunan cihaz {id, ...data} veya null
 */
export const findDuplicateDeviceBySerial = async (seriNumarasi, { excludeId } = {}) => {
    const normalized = normalizeSerial(seriNumarasi);
    if (!normalized) return null; // boş seri no'da mükerrer kontrolü yapma

    const snapshot = await getDocs(
        query(collection(db, 'devices'), where('seriNumarasiNormalized', '==', normalized))
    );

    let found = null;
    snapshot.forEach((docSnap) => {
        if (excludeId && docSnap.id === excludeId) return;
        found = { id: docSnap.id, ...docSnap.data() };
    });
    return found;
};

/**
 * Mükerrer cihaz tespit edildiğinde kullanıcıya gösterilecek standart uyarı metni.
 * @param {object} existing - bulunan mevcut cihaz
 * @param {string} seriNumarasi - kullanıcının girdiği seri no
 * @returns {string}
 */
export const duplicateDeviceMessage = (existing, seriNumarasi) => {
    const marka = `${existing.marka || ''} ${existing.model || ''}`.trim() || 'Cihaz';
    const sahip = existing.musteriAdSoyad || 'Bilinmiyor';
    return (
        `Bu seri numarası (${seriNumarasi}) sisteme zaten kayıtlı:\n` +
        `• ${marka}\n` +
        `• Mevcut sahibi: ${sahip}\n\n` +
        `Mükerrer kayıt oluşturulmadı. Lütfen mevcut cihazı kullanın ` +
        `(gerekiyorsa "Devret" ile sahibini değiştirin) veya seri numarasını kontrol edin.`
    );
};

/**
 * Zaten yüklü bir cihaz listesinde mükerrer seri numaralarını bulur (salt-okunur
 * rapor için; ekstra Firestore okuması yapmaz).
 * @param {Array} devices
 * @returns {{ duplicateSerials: Set<string>, countBySerial: Map<string, number> }}
 */
export const findDuplicateSerialsInList = (devices = []) => {
    const countBySerial = new Map();
    devices.forEach((d) => {
        const n = normalizeSerial(d.seriNumarasi);
        if (!n) return;
        countBySerial.set(n, (countBySerial.get(n) || 0) + 1);
    });
    const duplicateSerials = new Set();
    countBySerial.forEach((count, serial) => {
        if (count > 1) duplicateSerials.add(serial);
    });
    return { duplicateSerials, countBySerial };
};
